import {useState} from "react";
import {propertyTypeIconService} from "../services/propertyTypeIconService";
import {useToast} from "./common/useToast";

interface UsePropertyTypeIconReturn {
  uploadIcon: (propertyTypeId: number, file: File) => Promise<any>;
  deleteIcon: (propertyTypeId: number) => Promise<boolean>;
  isUploading: boolean;
  isDeleting: boolean;
  uploadError: string | null;
  deleteError: string | null;
}

export const usePropertyTypeIcon = (): UsePropertyTypeIconReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const {success, error} = useToast();

  const uploadIcon = async (propertyTypeId: number, file: File): Promise<any> => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Validate file
      if (!file) {
        throw new Error("Please select a file");
      }

      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        throw new Error("Please select a valid image file (JPEG, PNG, WEBP, GIF)");
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error("File size must be less than 5MB");
      }

      const result = await propertyTypeIconService.uploadIcon(propertyTypeId, file);
      
      success("Success", "Property type icon uploaded successfully");
      return result;
    } catch (err: any) {
      const errorMessage = err.message || "Failed to upload icon";
      setUploadError(errorMessage);
      error("Upload Failed", errorMessage);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const deleteIcon = async (propertyTypeId: number): Promise<boolean> => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const result = await propertyTypeIconService.deleteIcon(propertyTypeId);
      
      success("Success", "Property type icon deleted successfully");
      return result;
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete icon";
      setDeleteError(errorMessage);
      error("Delete Failed", errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    uploadIcon,
    deleteIcon,
    isUploading,
    isDeleting,
    uploadError,
    deleteError,
  };
};